<template>
  <div class="bg-white h-screen overflow-y-auto overflow-x-hidden">
    <loading :status="loading.main"></loading>
    <div class="w-full h:auto sm:h-14 border-b sm:px-3">
      <div class="sm:flex sm:justify-between">
        <div class="flex">
          <loading :status="loading.top"></loading>
          <button @click="$router.push('/admin/repair-orders')" class="btn p-2 md:p-4 rounded-none shadow-none" type="button">
            <svg-vue class="h-5 w-5" icon="font-awesome.chevron-left-regular"></svg-vue>
          </button>
          <div class="block" v-on-clickaway="closeActionDropdown">
            <div class="relative inline-block text-left">
              <button @click="toggleActionDropdown('technician')" class="btn p-2 md:p-4 rounded-none shadow-none" type="button">
                <svg-vue class="h-5 w-5" icon="font-awesome.user-tag-regular"></svg-vue>
              </button>
              <div class="origin-top-right absolute left-0 mt-1 w-56 rounded-md shadow-lg z-20" v-show="actions.technician">
                <div class="rounded-md bg-white shadow-xs">
                  <div class="py-1">
                    <template v-for="(technician, index) in userList">
                      <a
                        :key="index"
                        @click.prevent="assignUser(technician.id)"
                        class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                        href="#"
                        role="menuitem"
                        >{{ technician.name }}</a
                      >
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <button @click="toggleTab('isEdit')" class="btn p-2 md:p-4 rounded-none shadow-none" type="button">
            <svg-vue class="h-5 w-5" icon="font-awesome.edit-regular"></svg-vue>
          </button>
          <button @click="printInvoice" class="btn p-2 md:p-4 rounded-none shadow-none" type="button">
            <svg-vue class="h-5 w-5" icon="font-awesome.print-solid"></svg-vue>
          </button>
          <button @click="toggleTab('diagnosticInfo')" class="btn p-2 md:p-4 rounded-none shadow-none" type="button">
            <svg-vue class="h-5 w-5" icon="font-awesome.stethoscope-solid"></svg-vue>
          </button>
          <button @click="deleteRepairModal = true" class="btn p-2 md:p-4 rounded-none shadow-none" type="button">
            <svg-vue class="h-5 w-5" icon="font-awesome.trash-alt-regular"></svg-vue>
          </button>
        </div>
        <div class="flex items-center justify-end m-3 sm:m-0">
          <div class="text-2xl font-semibold">
            <router-link :to="`/workshop/track-repair/${repair.tracking}?${repair.uuid}`">#{{ repair.tracking }}</router-link>
          </div>

          <div class="px-3" v-if="repair.status">
            <div class="rounded-md inline-flex items-center px-2.5 py-0.5 font-medium border border-blue-900 text-blue-900">
              {{ repair.status.body }}
            </div>
            <div
              v-if="repair.is_device_collected"
              class="rounded-md inline-flex items-center px-2.5 py-0.5 font-medium border border-blue-900 text-green-900"
            >
              {{ $t('Collected') }}
            </div>
            <div class="rounded-md inline-flex items-center px-2.5 py-0.5 font-medium border border-blue-900 text-green-900">
              {{ repair.has_warranty ? $t('Warranty') : $t('No Warranty') }}
            </div>
            <div
              v-if="repair.is_archive"
              class="rounded-md inline-flex items-center px-2.5 py-0.5 font-medium border border-blue-900 text-green-900"
            >
              {{ $t('Archived') }}
            </div>
          </div>
          <template v-if="repair.is_lock">
            <svg-vue class="h-6 w-6 text-gray-500" icon="font-awesome.lock-alt-regular"></svg-vue>
          </template>
          <template v-else>
            <svg-vue class="h-6 w-6 text-green-500" icon="font-awesome.lock-open-alt-solid"></svg-vue>
          </template>
        </div>
      </div>
    </div>
    <div class="mb-15 flex flex-wrap">
      <div class="w-full sm:w-full md:w-full lg:w-1/2 xl:w-1/2">
        <div class="border-r">
          <div class="sm:flex sm:items-center py-3 max-w-full border-b">
            <div class="px-6 sm:pl-6 sm:pr-3 sm:flex-1 sm:w-3/4">
              <template v-if="repair.is_manual">
                <div class="text-xl truncate">[{{ repair.priority.name }}] [{{ $t('issue') }}: 1 ]</div>
                <div
                  v-if="repair.defects"
                  :title="$t('Issue to be fix')"
                  class="bg-red-600 inline-flex items-center px-2 py-0.5 mr-1 mb-1 rounded text-xs font-medium leading-4 text-white flex"
                >
                  <svg-vue class="h-8 w-8 text-white pr-2" icon="font-awesome.bug-solid"></svg-vue>
                  {{ repair.defects.title }}
                </div>
              </template>
              <template v-else>
                <div class="text-xl truncate">
                  [{{ repair.priority.name }}] [{{ repair.defects.length | pluralize([$t('issue'), $t('issues')]) }}:
                  {{ repair.defects.length }} ]
                </div>
                <template v-for="(defect, index) in repair.defects">
                  <div
                    :key="index"
                    :title="$t('Issue to be fix')"
                    class="bg-red-600 inline-flex items-center px-2 py-0.5 mr-1 mb-1 rounded text-xs font-medium leading-4 text-white flex"
                  >
                    <svg-vue class="h-8 w-8 text-white pr-2" icon="font-awesome.bug-solid"></svg-vue>
                    {{ defect.title }}
                  </div>
                </template>
              </template>
            </div>
            <div class="px-6 sm:pl-3 sm:pr-6 sm:flex-1 sm:w-1/4">
              <div class="flex items-center sm:float-right mt-3 sm:mt-0">
                <div class="text-sm sm:pr-2">
                  {{ repair.created_at }}
                </div>
                <template v-if="repair.is_lock">
                  {{ $t('Locked') }}
                </template>
                <template v-else>
                  <template v-if="repair.user_id">
                    <button @click="replyForm = true" class="flex items-center btn btn-secondary p-2 ml-3 sm:ml-0" type="button">
                      <svg-vue class="h-4 w-4 mr-2" icon="font-awesome.reply-regular"></svg-vue>
                      {{ $t('Update') }}
                    </button>
                  </template>
                  <template v-else>{{ $t('Unassigned') }}</template>
                </template>
              </div>
            </div>
          </div>
          <div class="p-1 border-b bg-yellow-300" v-show="replyForm">
            <loading :status="loading.reply"></loading>
            <form @submit.prevent="addReply">
              <input-wysiwyg
                :plugins="{ images: true, quickReply: true }"
                :quick-reply-list="quickReplyList"
                id="repair_log_body"
                v-model="logReply.body"
              >
                <template #bottom>
                  <div class="border mt-1 bg-white md:flex justify-between p-2">
                    <button @click="discardLogReply" class="btn btn-secondary" type="button">
                      {{ $t('Discard') }}
                    </button>
                    <div class="pt-1" v-if="repair.send_notification">
                      <input-switch
                        :disabled-label="$t('Disabled')"
                        :enabled-label="$t('Customer will be notifed')"
                        id="notify"
                        v-model="logReply.notify"
                      ></input-switch>
                    </div>
                    <div class="md:flex">
                      <select
                        aria-label="Sort by"
                        class="block form-select border bg-green-100 border-green-800 rounded-none bg-white text-sm leading-5 font-medium hover:text-gray-900 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150 rounded-l-md"
                        id="status"
                        v-model="logReply.status_id"
                      >
                        <template v-for="(status, index) in statusList">
                          <option :key="index" :value="status.id">
                            {{ status.body }}
                          </option>
                        </template>
                      </select>
                      <button class="btn btn-app rounded-none rounded-r-md" type="submit">
                        {{ $t('Update') }}
                      </button>
                    </div>
                  </div>
                </template>
              </input-wysiwyg>
            </form>
          </div>
          <template v-if="logs">
            <div class="block">
              <template v-if="!repair.send_notification">
                <div class="text-sm text-center bg-yellow-400">
                  {{ $t('This customer will not receive notifications via email or sms') }}
                </div>
              </template>
              <div class="text-xs text-white text-center bg-gray-900">
                {{ $t('Repair activity') }}
              </div>
              <template v-for="(log, index) in logs">
                <div :class="{ 'border-t': index > 0 }" :key="index" class="flex p-6 bg-white border-b-2">
                  <template v-if="log.user">
                    <img
                      :alt="$t('Avatar')"
                      :src="log.user.avatar !== 'gravatar' ? log.user.avatar : log.user.gravatar"
                      class="h-12 w-12 hidden sm:inline rounded-md"
                    />
                  </template>
                  <div class="sm:pl-6 pb-2 w-full">
                    <div class="md:flex md:items-center pb-1">
                      <div class="md:flex-1 text-sm font-semibold text-gray-800" v-if="log.user">
                        {{ log.user.name | capitalize }}
                        <span class="text-xs text-gray-500">({{ log.user.role_title }})</span>
                      </div>
                      <div class="md:flex-1">
                        <div class="md:float-right text-xs text-gray-500">
                          {{ log.created_at }}
                        </div>
                      </div>
                    </div>
                    <p class="text-gray-900 text-xs" v-html="log.body"></p>
                  </div>
                </div>
              </template>
            </div>
          </template>
          <template v-else>
            <div class="h-full flex border-t">
              <div class="m-auto">
                <div class="grid grid-cols-1 justify-items-center h-full w-full py-24">
                  <div class="flex justify-center items-center">
                    <svg-vue class="h-full h-auto w-48 mb-6" icon="undraw.task-list"></svg-vue>
                  </div>
                  <div class="flex justify-center items-center">
                    <div class="w-full font-semibold text-2xl">
                      {{ $t('This repair order has no logs') }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
      <div class="w-full sm:w-full md:w-full lg:w-1/2 xl:w-1/2">
        <div class="p-6">
          <template v-if="isEdit">
            <loading :status="loading.update"></loading>
            <form @submit.prevent="updateRepairOrder">
              <div class="grid grid-cols-3 gap-6">
                <div class="col-span-3">
                  <label class="form-label" for="name">{{ $t('Name') }}</label>
                  <div class="pt-1">
                    <input :placeholder="$t('Name')" class="form-input" id="name" required v-model="repair.name" />
                  </div>
                </div>
                <div class="col-span-3">
                  <label class="form-label" for="email">{{ $t('Email address') }}</label>
                  <div class="pt-1">
                    <input :placeholder="$t('Email address')" class="form-input" id="email" required type="email" v-model="repair.email" />
                  </div>
                </div>
                <div class="col-span-3">
                  <label class="form-label" for="phone">{{ $t('Phone Number') }}</label>
                  <div class="pt-1">
                    <input :placeholder="$t('Phone Number')" class="form-input" id="phone" v-model="repair.phone" />
                  </div>
                </div>
                <div class="col-span-3">
                  <label class="form-label" for="address">{{ $t('Home address') }}</label>
                  <div class="pt-1">
                    <input :placeholder="$t('Home address')" class="form-input" id="address" v-model="repair.address" />
                  </div>
                </div>
                <div class="col-span-3">
                  <label class="form-label" for="additional_amount">
                    {{ $t('Additional amount') }}
                  </label>
                  <div class="pt-1">
                    <input
                      :placeholder="$t('Additional amount')"
                      class="form-input"
                      id="additional_amount"
                      required
                      step="0.01"
                      type="number"
                      v-model="repair.additional_amount"
                    />
                  </div>
                </div>
                <div class="col-span-3">
                  <label class="form-label flex justify-between" for="pre_paid">
                    {{ $t('Paid Amount') }}
                    <small>{{ $t('Total amount') }} : {{ repair.total_charges | currency }}</small>
                    <small v-if="repair.due > 0">{{ $t('Due') }}: {{ repair.due | currency }}</small>
                  </label>
                  <div class="pt-1">
                    <input
                      :placeholder="$t('Paid Amount')"
                      class="form-input"
                      id="pre_paid"
                      required
                      step="0.01"
                      type="number"
                      v-model="repair.pre_paid"
                    />
                  </div>
                </div>
                <div class="col-span-3">
                  <label class="form-label" for="payment_info">{{ $t('Payment Detail') }}</label>
                  <input-wysiwyg id="payment_info" v-model="repair.payment_info"></input-wysiwyg>
                </div>
                <div class="col-span-3">
                  <label class="form-label" for="priority">{{ $t('Select priority') }}</label>
                  <select class="form-input" id="priority" v-model="repair.priority">
                    <option>{{ $t('Select priority') }}</option>
                    <option v-for="(priority, index) in priorityList" :key="index" :value="priority">
                      {{ priority.name }} {{ priority.extra_charge | currency }}
                    </option>
                  </select>
                </div>
                <template v-if="repair.is_manual">
                  <div class="col-span-3">
                    <label class="form-label" for="device_info_name">{{ $t('Device name') }}</label>
                    <div class="pt-1">
                      <input
                        :placeholder="$t('Device name')"
                        class="form-input rounded-0"
                        id="device_info_name"
                        v-model="repair.device_info.name"
                      />
                    </div>
                  </div>
                  <div class="col-span-3">
                    <label class="form-label" for="device_info_model">{{ $t('Device model') }}</label>
                    <div class="pt-1">
                      <input
                        :placeholder="$t('Device model')"
                        class="form-input rounded-0"
                        id="device_info_model"
                        v-model="repair.device_info.model"
                      />
                    </div>
                  </div>
                  <div class="col-span-3 md:flex md:space-x-2">
                    <div class="w-full">
                      <label class="form-label" for="defects_info_title">{{ $t('Issues') }}</label>
                      <div class="pt-1">
                        <input
                          :placeholder="$t('Issues or problems')"
                          class="form-input rounded-0"
                          id="defects_info_title"
                          v-model="repair.defects_info.title"
                        />
                      </div>
                    </div>
                    <div class="w-full">
                      <label class="form-label" for="defects_info_price">{{ $t('Price or charges') }}</label>
                      <div class="pt-1">
                        <input
                          :placeholder="$t('Price or charges')"
                          class="form-input rounded-0"
                          id="defects_info_price"
                          type="number"
                          v-model="repair.defects_info.price"
                          min="0"
                          required
                        />
                      </div>
                    </div>
                    <div class="w-full">
                      <label class="form-label" for="defects_info_cost">{{ $t('Cost') }}</label>
                      <div class="pt-1">
                        <input
                          :placeholder="$t('Cost')"
                          class="form-input rounded-0"
                          type="number"
                          id="defects_info_cost"
                          v-model="repair.defects_info.cost"
                          min="0"
                          required
                        />
                      </div>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <div class="col-span-3">
                    <label class="form-label" for="brand">{{ $t('Select brand') }}</label>
                    <select class="form-input" id="brand" v-model="repair.brand_id" @change.prevent="getDevices" required>
                      <option>{{ $t('Select device brand') }}</option>
                      <option v-for="(brand, index) in brandList" :key="index" :value="brand.id">{{ brand.name }}</option>
                    </select>
                  </div>
                  <div class="col-span-3">
                    <label class="form-label" for="device">{{ $t('Select device') }}</label>
                    <select class="form-input" id="device" v-model="repair.device_id" @change.prevent="getDefects" required>
                      <option>{{ $t('Select device') }}</option>
                      <option v-for="(device, index) in deviceList" :key="index" :value="device.id">{{ device.name }}</option>
                    </select>
                  </div>
                  <div class="col-span-3">
                    <label class="form-label" for="defect">{{ $t('Select device defect') }}</label>
                    <select class="form-input" id="defect" multiple v-model="repair.defects" required>
                      <option>{{ $t('Select device defect') }}</option>
                      <option v-for="(defect, index) in defectList" :key="index" :value="defect">
                        {{ defect.title }} {{ defect.price | currency }}
                      </option>
                    </select>
                  </div>
                </template>
                <div class="col-span-3">
                  <label class="form-label" for="is_device_collected">{{ $t('Device collected ?') }}</label>
                  <input-switch
                    :disabled-label="$t('Device has not yet received')"
                    :enabled-label="$t('Device has been successfully received')"
                    id="is_device_collected"
                    v-model="repair.is_device_collected"
                  ></input-switch>
                  <div class="form-description-text">
                    {{ $t('This option will control to bypass the payment gateways while repair booking') }}.
                  </div>
                </div>
                <div class="col-span-3">
                  <label class="form-label" for="send_notification">{{ $t('Send notifications ?') }}</label>
                  <input-switch
                    :disabled-label="$t('The customer will not receive notifications')"
                    :enabled-label="$t('The customer will receive notifications')"
                    id="send_notification"
                    v-model="repair.send_notification"
                  ></input-switch>
                  <div class="form-description-text">
                    {{ $t('This option will control whether the customer is receiving notifications or not on every update.') }}.
                  </div>
                </div>

                <div class="col-span-3">
                  <label class="form-label" for="is_lock">{{ $t('Lock') }}</label>
                  <input-switch
                    :disabled-label="$t('Unlocked')"
                    :enabled-label="$t('Locked')"
                    id="is_lock"
                    v-model="repair.is_lock"
                  ></input-switch>
                  <div class="form-description-text">{{ $t('This option will control modifications ') }}.</div>
                </div>
                <template v-if="repair.is_lock">
                  <div class="col-span-3">
                    <label class="form-label" for="is_archive">{{ $t('Archive') }}</label>
                    <input-switch
                      :disabled-label="$t('No')"
                      :enabled-label="$t('Yes')"
                      id="is_archive"
                      v-model="repair.is_archive"
                    ></input-switch>
                    <div class="form-description-text">{{ $t('This option will control repair order appearance in list') }}.</div>
                  </div>
                </template>
                <div class="col-span-3">
                  <label class="form-label" for="has_warranty">{{ $t('Warranty') }}</label>
                  <input-switch
                    :disabled-label="$t('No')"
                    :enabled-label="$t('Yes')"
                    id="has_warranty"
                    v-model="repair.has_warranty"
                  ></input-switch>
                  <div class="form-description-text">{{ $t('This option will control warranty status of repairable device') }}.</div>
                </div>
                <div class="col-span-3">
                  <label class="form-label" for="serial_number">{{ $t('Serial number') }}</label>
                  <div class="pt-1">
                    <input
                      :placeholder="$t('Serial number')"
                      class="form-input"
                      id="serial_number"
                      type="text"
                      v-model="repair.serial_number"
                    />
                  </div>
                  <small class="form-description-text">{{ $t('press tab key to see save button') }}</small>
                </div>
                <div class="col-span-3 flex justify-between">
                  <button @click="toggleTab('repairInfo')" class="btn btn-secondary">
                    {{ $t('Cancel') }}
                  </button>
                  <button class="btn btn-app" type="submit">
                    {{ $t('Save') }}
                  </button>
                </div>
              </div>
            </form>
          </template>
          <template v-if="repairInfo">
            <template v-if="!repair.is_device_collected">
              <div class="text-xs bg-red-100 rounded-md border-l-4 border-red-500 mb-3 text-red-700 p-4" role="alert">
                <loading :status="loading.reminder"></loading>
                <p class="font-bold">{{ 'Attention' }}</p>
                <p>
                  {{ $t('The defective device has not been yet received from the customer send him reminder with dispatch information') }}.
                </p>
                <button class="btn btn-secondary" @click="sendReminder">{{ $t('Send reminder') }}</button>
                <button class="btn btn-app" @click="toggleTab('isEdit')">{{ $t('OR update status') }}</button>
              </div>
            </template>
            <div class="flex">
              <img :alt="$t('Avatar')" :src="repair.device.avatar" class="h-14 w-14" />
            </div>
            <div class="mt-2 pb-2">
              <div class="text-gray-900 text-xs truncate">
                {{ $t('Device information') }}
              </div>
              <div class="flex items-center text-sm leading-5">
                <span class="truncate">
                  <strong>{{ $t('Brand') }} :</strong>
                  {{ repair.brand.name | capitalize }}
                </span>
              </div>
              <div class="flex items-center text-sm leading-5">
                <span class="truncate">
                  <strong>{{ $t('Name') }} :</strong>
                  {{ repair.device.name | capitalize }}
                </span>
              </div>
              <div class="flex items-center text-sm leading-5">
                <span class="truncate">
                  <strong>{{ $t('Model') }} :</strong>
                  {{ repair.device.model | capitalize }}
                </span>
              </div>
              <div class="flex items-center text-sm leading-5">
                <span class="truncate">
                  <strong>{{ $t('Serial number') }} :</strong>
                  {{ repair.serial_number }}
                </span>
              </div>
            </div>
            <div class="mt-2 pb-2">
              <div class="text-gray-900 text-xs truncate">
                {{ $t('Customer information') }}
              </div>
              <div class="flex items-center text-sm leading-5">
                <span class="truncate">
                  <strong>{{ $t('Name') }} :</strong>
                  {{ repair.name | capitalize }}
                </span>
              </div>
              <div class="flex items-center text-sm leading-5">
                <span class="truncate">
                  <strong>{{ $t('Email') }} :</strong>
                  {{ repair.email }}
                </span>
              </div>
              <div class="flex items-center text-sm leading-5">
                <span class="truncate">
                  <strong>{{ $t('Phone') }} :</strong>
                  {{ repair.phone }}
                </span>
              </div>
              <div class="flex items-center text-sm leading-5">
                <span class="truncate">
                  <strong>{{ $t('Address') }} :</strong>
                  {{ repair.address }}
                </span>
              </div>
            </div>
            <div class="mt-2 pb-2">
              <div class="text-gray-900 text-xs truncate">
                {{ $t('Payment information') }}
              </div>
              <div class="flex items-center text-sm leading-5">
                <span class="truncate">
                  <strong>{{ $t('Subtotal') }} :</strong>
                  {{ repair.sub_total | currency }}
                </span>
              </div>
              <div class="flex items-center text-sm leading-5">
                <span class="truncate">
                  <strong>{{ $t('Priority charges') }} :</strong>
                  {{ repair.priority.extra_charge | currency }} [{{ repair.priority.name }}]
                </span>
              </div>
              <div class="flex items-center text-sm leading-5">
                <span class="truncate">
                  <strong>{{ $t('Tax') }} :</strong>
                  {{ repair.tax | currency }}
                </span>
              </div>
              <div class="flex items-center text-sm leading-5">
                <span class="truncate">
                  <strong>{{ $t('Total amount') }} :</strong>
                  {{ repair.total_charges | currency }}
                </span>
              </div>
              <div class="flex items-center text-sm leading-5">
                <span class="truncate">
                  <strong>{{ $t('Additional amount') }} :</strong>
                  {{ repair.additional_amount | currency }}
                </span>
              </div>
              <div class="flex items-center text-sm mt-2 leading-5">
                <span class="truncate">
                  <strong>{{ $t('Profit gain') }} :</strong>
                  {{ repair.profit | currency }}
                </span>
              </div>
              <div class="flex items-center text-sm mt-5 leading-5">
                <span class="truncate">
                  <strong class="text-green-600">{{ $t('Prepaid') }} :</strong>
                  {{ repair.pre_paid | currency }}
                </span>
              </div>
              <div class="flex items-center text-sm leading-5">
                <span class="truncate">
                  <strong class="text-red-600">{{ $t('Due') }} :</strong>
                  {{ repair.due | currency }}
                </span>
              </div>
              <div class="text-sm mt-2 leading-5">
                <strong>{{ $t('Detail') }} :</strong>
                <br />
                <div class="text-justify" v-html="repair.payment_info"></div>
              </div>
            </div>
          </template>
          <template v-if="diagnosticInfo">
            <strong>{{ $t('Initial diagnostics') }}</strong>
            <div class="p-2" v-html="repair.diagnostics"></div>
            <button @click="toggleTab('repairInfo')" class="btn btn-secondary pt-3">
              {{ $t('Back') }}
            </button>
          </template>
        </div>
      </div>
    </div>
    <!-- delete model -->
    <repair-order-delete-model :show="deleteRepairModal" @cancelled="deleteRepairModal = false" @confirmed="deleteRepairOrder">
      <template #title>{{ $t('Repair order delete') }}</template>
      {{ $t('Are you sure you want to delete the repair order?') }}
      {{ $t('All data will be permanently removed') }}. {{ $t('All related data will be deleted') }}.
    </repair-order-delete-model>
    <!-- end delete model -->
  </div>
</template>
<script>
import { mixin as clickaway } from 'vue-clickaway';
import confirmationModel from '@/components/layout/admin/confirmation';

export default {
  name: 'manage',
  mixins: [clickaway],
  components: {
    RepairOrderDeleteModel: confirmationModel,
  },
  metaInfo() {
    return {
      title: this.$i18n.t('Manage repair order'),
    };
  },
  mounted() {
    this.getBrandAndPriorityList();
    this.getRepairOrder();
    this.getOrderFilters();
    this.getQuickReplies();
  },
  data() {
    return {
      loading: {
        main: true,
        top: false,
        form: true,
        reply: false,
        update: false,
        reminder: false,
      },
      deleteRepairModal: false,
      isEdit: false,
      diagnosticInfo: false,
      repairInfo: false,
      replyForm: false,
      tax: {
        tax_rate: 0,
        is_tax_fix: false,
        is_tax_included: false,
      },
      repair: {
        is_archive: false,
        is_lock: false,
        has_warranty: false,
        is_device_collected: false,
        send_notification: false,
        diagnostics: '',
        due: 0,
        payment_info: null,
        pre_paid: 0,
        additional_amount: 0,
        defects: [],
        device: {
          avatar: null,
          name: null,
          model: null,
        },
        brand: {
          name: null,
        },
        priority: {
          name: null,
          extra_charge: 0,
        },
      },
      logs: [],
      logReply: {
        repair_order_id: null,
        user_id: null,
        status_id: null,
        notify: false,
        body: '',
      },
      actions: {
        technician: false,
      },

      quickReplyList: [],
      userList: [],
      statusList: [],
      priorityList: [],
      brandList: [],
      deviceList: [],
      defectList: [],
      currentDefects: [],
      currentDevice: null,
      currentBrand: null,
    };
  },
  computed: {
    repairCostAmount() {
      let amount = 0;
      if (this.repair.is_manual) {
        amount = this.repair.defects_info.cost;
      } else {
        this.repair.defects.forEach((defect) => {
          amount += defect.cost;
        });
      }
      return amount;
    },
    subTotalAmount() {
      let amount = 0;
      if (this.repair.is_manual) {
        amount = this.repair.defects_info.price;
      } else {
        this.repair.defects.forEach((defect) => {
          amount += defect.price;
        });
      }
      return amount;
    },
    profit() {
      return Number(this.totalAmountWithTax - this.repairCostAmount - this.taxAmount);
    },
    subTotalWithPriority() {
      const amount = Number(this.subTotalAmount) + Number(this.repair.priority.extra_charge);
      return amount;
    },
    taxAmount() {
      const amount = this.subTotalWithPriority;
      const rate = Number(this.tax.tax_rate ?? this.tax.rate);
      if (this.tax.is_vat) {
        if (this.tax.is_tax_included) {
          const vatAmount = amount - amount / (1 + rate / 100);
          return vatAmount.toFixed(2);
        } else {
          const vatAmount = amount * (rate / 100);
          return vatAmount.toFixed(2);
        }
      } else {
        if (this.tax.is_tax_fix) {
          return rate;
        }
        return amount * (rate / 100);
      }
    },
    totalAmountWithTax() {
      if (this.tax.is_tax_included) {
        return this.subTotalWithPriority;
      }
      return this.subTotalWithPriority + Number(this.taxAmount);
    },
  },
  methods: {
    getDefects() {
      this.loading.main = true;
      this.defectList = [];
      axios
        .post(route('repair.defect.list'), {
          device_id: this.repair.device_id,
        })
        .then((response) => {
          this.defectList = response.data;
          this.loading.main = false;
        })
        .catch(() => {
          this.loading.main = false;
        });
    },
    getDevices() {
      this.loading.main = true;
      this.defectList = [];
      this.deviceList = [];
      axios
        .post(route('repair.device.list'), { brand_id: this.repair.brand_id })
        .then((response) => {
          this.loading.main = false;
          this.deviceList = response.data;
        })
        .catch(() => {
          this.loading.main = false;
        });
    },
    getBrandAndPriorityList() {
      this.loading.main = true;
      this.deviceList = [];
      this.defectList = [];
      axios
        .get(route('repair.brand.list'))
        .then((response) => {
          this.priorityList = response.data.priorities;
          this.brandList = response.data.brands;
          this.getTax();
          this.loading.main = false;
        })
        .catch(() => {
          this.loading.main = false;
        });
    },
    sendReminder() {
      this.loading.reminder = true;
      axios
        .post(route('repair.send.reminder', this.$route.params.id))
        .then((response) => {
          this.$notify({
            title: this.$i18n.t('Success').toString(),
            text: response.data.message.toString(),
            type: 'success',
          });
          this.loading.reminder = false;
        })
        .catch(() => {
          this.loading.reminder = false;
        });
    },
    toggleTab(tab) {
      if (tab === 'diagnosticInfo') {
        this.diagnosticInfo = true;
        this.isEdit = false;
        this.repairInfo = false;
      }
      if (tab === 'isEdit') {
        this.isEdit = true;
        this.diagnosticInfo = false;
        this.repairInfo = false;
      }
      if (tab === 'repairInfo') {
        this.repairInfo = true;
        this.diagnosticInfo = false;
        this.isEdit = false;
      }
    },
    printInvoice() {
      this.$router.push(`/print/repair-order/${this.repair.uuid}`);
    },
    updateRepairOrder() {
      this.loading.update = true;
      axios
        .patch(route('repair-orders.update', this.$route.params.id), {
          name: this.repair.name,
          email: this.repair.email,
          phone: this.repair.phone,
          address: this.repair.address,
          pre_paid: this.repair.pre_paid,
          serial_number: this.repair.serial_number,
          payment_info: this.repair.payment_info,
          additional_amount: this.repair.additional_amount,
          is_device_collected: this.repair.is_device_collected,
          send_notification: this.repair.send_notification,
          is_archive: this.repair.is_archive,
          is_lock: this.repair.is_lock,
          repair_priority_id: this.repair.priority.id,
          has_warranty: this.repair.has_warranty,
          sub_total: this.subTotalAmount,
          total_charges: this.totalAmountWithTax,
          profit: this.profit,
          total_cost: this.repairCostAmount,
          tax: this.taxAmount,
          brand_id: this.repair.brand_id ?? null,
          device_id: this.repair.device_id ?? null,
          defects: this.repair.defects ?? null,
          brand_info: this.repair.brand_info ?? null,
          device_info: this.repair.device_info ?? null,
          defects_info: this.repair.defects_info ?? null,
          is_manual: this.repair.is_manual ? true : false,
        })
        .then((response) => {
          this.$notify({
            title: this.$i18n.t('Success').toString(),
            text: response.data.message.toString(),
            type: 'success',
          });
          this.repair = response.data.repair;
          if (this.repair.is_manual) {
            this.repair.brand_info = this.repair.brand;
            this.repair.device_info = this.repair.device;
            this.repair.defects_info = this.repair.defects;
          } else {
            this.repair.device_id = this.repair.device.id;
            this.repair.brand_id = this.repair.brand.id;
          }
          this.loading.update = false;
          this.toggleTab('repairInfo');
          this.$nprogress.done();
        })
        .catch(() => {
          this.loading.update = false;
        });
    },
    getTax() {
      this.loading.main = true;
      axios
        .get(route('settings.get.tax'))
        .then((response) => {
          this.tax = response.data;
          this.loading.main = false;
        })
        .catch(() => {
          this.loading.main = false;
        });
    },
    getRepairOrder() {
      this.loading.form = true;
      axios
        .get(route('repair-orders.show', this.$route.params.id))
        .then((response) => {
          this.loading.form = false;
          this.repair = response.data.repair;
          this.logs = response.data.logs;
          this.logReply.status_id = response.data.repair.status.id;
          if (this.repair.is_manual) {
            this.repair.brand_info = this.repair.brand;
            this.repair.device_info = this.repair.device;
            this.repair.defects_info = this.repair.defects;
          } else {
            this.repair.device_id = this.repair.device.id;
            this.repair.brand_id = this.repair.brand.id;
            this.getDevices();
            this.getDefects();
          }
          this.loading.main = false;
          this.toggleTab('repairInfo');
        })
        .catch(() => {
          this.$router.push('/admin/repair-orders');
        });
    },
    getQuickReplies() {
      axios.get(route('quick-replies.list')).then((response) => {
        this.quickReplyList = response.data;
      });
    },
    getOrderFilters() {
      axios.get(route('repair-orders.filters')).then((response) => {
        this.userList = response.data.technicians;
        this.statusList = response.data.statuses;
        this.priorityList = response.data.priorities;
      });
    },
    discardLogReply() {
      this.logReply.body = '';
      this.replyForm = false;
    },

    addReply() {
      this.loading.reply = true;
      this.logReply.repair_order_id = this.repair.id;
      this.logReply.user_id = this.repair.user_id;
      axios
        .post(route('repair-log.store'), this.logReply)
        .then((response) => {
          this.$notify({
            title: this.$i18n.t('Success').toString(),
            text: response.data.message.toString(),
            type: 'success',
          });
          this.discardLogReply();
          this.getRepairOrder();
          this.loading.reply = false;
        })
        .catch(() => {
          this.loading.reply = false;
        });
    },
    closeActionDropdown() {
      this.actions.technician = false;
    },
    toggleActionDropdown() {
      this.actions.technician = !this.actions.technician;
    },
    assignUser(value) {
      this.loading.top = true;
      axios
        .post(route('repair-orders.assign.user', this.$route.params.id), {
          user: value,
        })
        .then((response) => {
          this.closeActionDropdown();
          this.repair.user_id = value;
          this.$notify({
            title: this.$i18n.t('Success').toString(),
            text: response.data.message.toString(),
            type: 'success',
          });
          this.loading.top = false;
        })
        .catch(() => {
          this.closeActionDropdown();
          this.loading.top = false;
        });
    },
    deleteRepairOrder() {
      this.loading.top = true;
      axios
        .delete(route('repair-orders.destroy', this.$route.params.id))
        .then((response) => {
          this.$notify({
            title: this.$i18n.t('Success').toString(),
            text: response.data.message.toString(),
            type: 'success',
          });
          this.$router.push('/admin/repair-orders');
        })
        .catch(() => {
          this.loading.top = false;
          this.deleteRepairModal = false;
        });
    },
  },
};
</script>
